import { useCallback, type MutableRefObject } from "react";
import logger from "@shared/utils/logger";
import type { EEGTileTransportMode } from "../services/EEGSegmentCache";
import {
  resolveEEGViewerRequestedTime,
  shouldResetEEGViewerWindowStartOnSeek,
} from "../components/eegViewerPlaybackHelpers";

/**
 * Seek / paused-inspect navigation helpers extracted from EEGViewer to keep
 * the main component focused on rendering. Owns the deferred-paused-seek
 * ref (shared with EEGViewer's playback tick machinery) plus the page /
 * fine-pan / go-to-start navigation helpers and the logic that resolves a
 * previously-deferred paused seek once the target buffer window has loaded.
 *
 * `jumpToViewerTime` itself stays in EEGViewer — it is deeply coupled to the
 * tile/buffer lifecycle (pending buffer flushes, renderer blit cache,
 * prefetch scheduling, etc.) that also backs `handlePlaybackTick`, and is
 * passed into this hook rather than imported to avoid a circular import.
 */

function getFinePanStepSeconds(timeScaleSeconds: number): number {
  return Math.max(1, Math.round((timeScaleSeconds || 10) / 10));
}

export type JumpToViewerTimeReason = {
  source?: string;
  direction?: string;
  pageCount?: number;
  stepSeconds?: number;
};

export type JumpToViewerTimeFn = (
  targetTime: number,
  reason?: JumpToViewerTimeReason,
) => void;

export interface UseEEGViewerSeekParams {
  // Created via a plain `useRef` in EEGViewer (rather than inside this hook)
  // because `jumpToViewerTime` — which stays in EEGViewer — reads/writes it
  // and is defined before this hook can be called (it is itself passed in
  // below). Sharing the same ref object keeps both sides in sync.
  pendingPausedSeekTimeRef: MutableRefObject<number | null>;
  currentConfigTimeScale: number | undefined;
  currentConfigPlaybackSpeed: number | undefined;
  currentTimeRef: MutableRefObject<number>;
  rendererLoadedWindowStartRef: MutableRefObject<number | null>;
  rendererLoadedBufferEndRef: MutableRefObject<number | null>;
  recordingMetaDurationRef: MutableRefObject<number>;
  recordingDurationRef: MutableRefObject<number>;
  dataWindowStartRef: MutableRefObject<number>;
  playbackTransportMode: EEGTileTransportMode;
  getEffectiveBufferSize: (
    speed: number,
    timeScale: number,
    transportMode?: EEGTileTransportMode,
  ) => number;
  setCurrentTime: (time: number) => number;
  jumpToViewerTime: JumpToViewerTimeFn;
}

export function useEEGViewerSeek(params: UseEEGViewerSeekParams) {
  const {
    pendingPausedSeekTimeRef,
    currentConfigTimeScale,
    currentConfigPlaybackSpeed,
    currentTimeRef,
    rendererLoadedWindowStartRef,
    rendererLoadedBufferEndRef,
    recordingMetaDurationRef,
    recordingDurationRef,
    dataWindowStartRef,
    playbackTransportMode,
    getEffectiveBufferSize,
    setCurrentTime,
    jumpToViewerTime,
  } = params;

  const navigateByPages = useCallback(
    (direction: 1 | -1, pageCount: number) => {
      const pageSize = currentConfigTimeScale || 10;
      const navigationBaseTime = resolveEEGViewerRequestedTime({
        currentTime: currentTimeRef.current,
        pendingSeekTime: pendingPausedSeekTimeRef.current,
      });
      jumpToViewerTime(navigationBaseTime + direction * pageSize * pageCount, {
        source: "page-nav",
        direction: direction > 0 ? "forward" : "backward",
        pageCount,
        stepSeconds: pageSize,
      });
    },
    [currentConfigTimeScale, currentTimeRef, jumpToViewerTime],
  );

  const navigateByFinePan = useCallback(
    (direction: 1 | -1) => {
      const step = getFinePanStepSeconds(currentConfigTimeScale || 10);
      const navigationBaseTime = resolveEEGViewerRequestedTime({
        currentTime: currentTimeRef.current,
        pendingSeekTime: pendingPausedSeekTimeRef.current,
      });
      jumpToViewerTime(navigationBaseTime + direction * step, {
        source: "fine-pan",
        direction: direction > 0 ? "forward" : "backward",
        stepSeconds: step,
      });
    },
    [currentConfigTimeScale, currentTimeRef, jumpToViewerTime],
  );

  const goToStart = useCallback(() => {
    jumpToViewerTime(0, { source: "go-to-start" });
  }, [jumpToViewerTime]);

  const consumeDeferredPausedSeekTime = useCallback(() => {
    const pendingSeekTime = pendingPausedSeekTimeRef.current;
    if (pendingSeekTime === null) {
      return currentTimeRef.current;
    }

    const timeScale = currentConfigTimeScale ?? 10;
    const speed = currentConfigPlaybackSpeed ?? 1;
    const bufferSize = getEffectiveBufferSize(
      speed,
      timeScale,
      playbackTransportMode,
    );
    const loadedWindowStart = rendererLoadedWindowStartRef.current;
    const loadedBufferEnd = rendererLoadedBufferEndRef.current;
    let seekWindowReady = !shouldResetEEGViewerWindowStartOnSeek({
      targetTime: pendingSeekTime,
      dataWindowStart: loadedWindowStart ?? dataWindowStartRef.current,
      bufferSize,
      timeScale,
      loadedWindowStart,
      loadedBufferEnd,
    });
    const recordingEnd =
      recordingMetaDurationRef.current > 0
        ? recordingMetaDurationRef.current
        : recordingDurationRef.current > 0
          ? recordingDurationRef.current
          : 0;
    const maxSeekTime =
      recordingEnd > 0 ? Math.max(0, recordingEnd - timeScale) : null;
    const isNearRecordingEnd =
      maxSeekTime !== null && pendingSeekTime >= maxSeekTime - 0.5;
    const loadedCoversRecordingEnd =
      loadedBufferEnd !== null &&
      recordingEnd > 0 &&
      loadedBufferEnd >= recordingEnd - 0.5;
    if (!seekWindowReady && isNearRecordingEnd && loadedCoversRecordingEnd) {
      seekWindowReady = true;
    }

    logger.debug("[ANNOTATION_SEEK] consumeDeferredPausedSeekTime", {
      pendingSeekTime,
      seekWindowReady,
      loadedWindowStart,
      loadedBufferEnd,
      timeScale,
      currentTime: currentTimeRef.current,
    });

    if (!seekWindowReady) {
      logger.debug(
        "[ANNOTATION_SEEK] consumeDeferredPausedSeekTime → NOT READY yet, keeping deferred seek",
        {
          pendingSeekTime,
          loadedWindowStart,
          loadedBufferEnd,
        },
      );
      return currentTimeRef.current;
    }

    pendingPausedSeekTimeRef.current = null;
    const resolvedSeekTime = setCurrentTime(pendingSeekTime);
    currentTimeRef.current = resolvedSeekTime;

    logger.debug(
      "[ANNOTATION_SEEK] consumeDeferredPausedSeekTime → APPLIED deferred seek!",
      {
        pendingSeekTime,
        resolvedSeekTime,
      },
    );

    return resolvedSeekTime;
  }, [
    currentConfigPlaybackSpeed,
    currentConfigTimeScale,
    currentTimeRef,
    dataWindowStartRef,
    getEffectiveBufferSize,
    playbackTransportMode,
    recordingDurationRef,
    recordingMetaDurationRef,
    rendererLoadedBufferEndRef,
    rendererLoadedWindowStartRef,
    setCurrentTime,
  ]);

  return {
    pendingPausedSeekTimeRef,
    navigateByPages,
    navigateByFinePan,
    goToStart,
    consumeDeferredPausedSeekTime,
  };
}
